import React from 'react';
import { Dices, Loader2, RefreshCw } from 'lucide-react';
import { FinancialValue } from '../shared/FinancialValue';

interface MonteCarloCardProps {
  data?: {
    iterations: number;
    results: {
      base_claim: number;
      results: {
        mean: number;
        median: number;
        std_dev: number;
        ci_95_low: number;
        ci_95_high: number;
        min: number;
        max: number;
      };
    };
    run_at: string;
  };
  monteCarloLoading: boolean;
  onRunAnalysis: () => void;
}

export const MonteCarloCard: React.FC<MonteCarloCardProps> = ({
  data,
  monteCarloLoading,
  onRunAnalysis,
}) => {
  const mc = data;
  const mcResults = mc?.results?.results;

  return (
    <div className="rounded-xl p-6 shadow-sm" style={{ backgroundColor: 'var(--color-bg-surface)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Dices size={22} />
          Monte Carlo Simulation
        </h2>
        {mc && (
          <span className="px-3 py-1 rounded-full text-sm font-medium" style={{ backgroundColor: 'transparent', color: 'var(--color-verified)' }}>✓ Complete</span>
        )}
      </div>

      {/* Explanation */}
      <div className="rounded-lg p-4 mb-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <strong>What is Monte Carlo Simulation?</strong> This runs 1,000 random variations of your claim calculation to determine the statistical probability of different outcomes. The 95% confidence interval means there's a 95% chance the true claim value falls within this range — providing strong statistical backing for your claim.
        </p>
      </div>
      
      {mc ? (
        <div className="space-y-4">
          {/* Results Display */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Iterations</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>{mc.iterations?.toLocaleString()}</p>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Mean</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={mcResults?.mean} /></p>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Median</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={mcResults?.median} /></p>
            </div>
            <div className="rounded-lg p-4 text-center" style={{ backgroundColor: 'var(--color-bg-elevated)' }}>
              <p className="text-xs uppercase tracking-wide mb-1" style={{ color: 'var(--color-text-label)' }}>Std Dev</p>
              <p className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={mcResults?.std_dev} /></p>
            </div>
          </div>

          {/* 95% Confidence Interval */}
          <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--color-needs-human-tint)' }}>
            <p className="text-sm mb-3 font-medium" style={{ color: 'var(--color-text-secondary)' }}>95% Confidence Interval</p>
            <div className="flex items-center gap-3">
              <div className="text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>Low</p>
                <p className="text-sm font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={mcResults?.ci_95_low} /></p>
              </div>
              <div className="flex-1 relative">
                <div className="h-4 rounded-full" style={{ backgroundColor: 'var(--color-needs-human-tint-bd)' }}></div>
                <div className="absolute left-1/2 top-0 w-1 h-4 rounded transform -translate-x-1/2" title="Median" style={{ backgroundColor: 'var(--color-brand)' }}></div>
              </div>
              <div className="text-center">
                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>High</p>
                <p className="text-sm font-bold" style={{ color: 'var(--color-needs-human-text)' }}><FinancialValue value={mcResults?.ci_95_high} /></p>
              </div>
            </div>
            <p className="text-xs mt-2 text-center" style={{ color: 'var(--color-text-muted)' }}>
              There's a 95% probability your true claim value falls within this range
            </p>
          </div>

          <button
            onClick={onRunAnalysis}
            disabled={monteCarloLoading}
            className="w-full py-2 px-4 rounded-lg text-sm font-medium transition disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
          >
            {monteCarloLoading ? <><Loader2 size={14} className="animate-spin" /> Re-running...</> : <><RefreshCw size={14} /> Re-run Simulation</>}
          </button>

          <p className="text-xs" style={{ color: 'var(--color-text-dim)' }}>Last run: {new Date(mc.run_at).toLocaleString()}</p>
        </div>
      ) : (
        <div className="space-y-4">
          <button
            onClick={onRunAnalysis}
            disabled={monteCarloLoading}
            className="w-full py-3 px-4 rounded-lg font-bold transition disabled:opacity-50"
            style={{ backgroundColor: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          >
            {monteCarloLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                </svg>
                Running 1,000 Simulations...
              </span>
            ) : 'Run Monte Carlo Simulation (1,000 iterations)'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MonteCarloCard;
